import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Switch,
  Collapse,
  InputNumber,
  Space,
  message,
  Spin,
  Divider,
  Alert,
} from "antd";
import {
  createPulseReport,
  updatePulseReport,
  getPulseReportById,
  getAvailableVariables,
  previewPulseReport,
} from "@api/pulse-reports/pulseReport";
import { getHtmlTemplates } from "@api/email/htmlTemplate";
import { getAllPlans } from "@api/subscription/plan";
import { useQuery, useQueryClient } from "react-query";

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Report types
const reportTypes = [
  { value: "invoice", label: "Invoice Report" },
  { value: "usage", label: "Usage Report" },
  { value: "performance", label: "Performance Report" },
  { value: "analytics", label: "Analytics Report" },
  { value: "digest", label: "Weekly/Monthly Digest" },
  { value: "newsletter", label: "Newsletter" },
  { value: "product_update", label: "Product Update" },
  { value: "custom", label: "Custom Report" },
];

// Schedule types
const scheduleTypes = [
  { value: "on_refill", label: "On Subscription Refill" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "manual", label: "Manual Only" },
];

// Days of week
const daysOfWeek = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Target types
const targetTypes = [
  { value: "all", label: "All Users" },
  { value: "planBased", label: "Users by Plan" },
  { value: "targeted", label: "Specific Users" },
  { value: "dateRange", label: "Users by Join Date" },
  { value: "external", label: "External Recipients" },
];

const PulseReportForm = ({ history, mode = "create", pulseReportId }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleType, setScheduleType] = useState("monthly");
  const [targetType, setTargetType] = useState("all");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch existing pulse report for edit mode
  const { data: pulseReportData, isLoading: loadingPulseReport } = useQuery(
    ["pulseReport", pulseReportId],
    () => getPulseReportById(pulseReportId),
    {
      enabled: mode === "edit" && !!pulseReportId,
      onSuccess: (response) => {
        const data = response.data;
        form.setFieldsValue({
          name: data.name,
          description: data.description,
          reportType: data.reportType,
          htmlTemplate: data.htmlTemplate?._id || data.htmlTemplate,
          targetType: data.targetType,
          includedPlans: data.includedPlans?.map(p => p._id || p),
          scheduleType: data.schedule?.type,
          dayOfWeek: data.schedule?.dayOfWeek,
          dayOfMonth: data.schedule?.dayOfMonth,
          timeHour: data.schedule?.timeOfDay?.hour || 9,
          timeMinute: data.schedule?.timeOfDay?.minute || 0,
          emailSubject: data.emailSettings?.subject,
          fromName: data.emailSettings?.fromName,
          replyTo: data.emailSettings?.replyTo,
          // Data sections
          billingEnabled: data.dataSections?.billing?.enabled,
          usageEnabled: data.dataSections?.usage?.enabled,
          performanceEnabled: data.dataSections?.performance?.enabled,
          analyticsEnabled: data.dataSections?.analytics?.enabled,
          teamEnabled: data.dataSections?.team?.enabled,
        });
        setScheduleType(data.schedule?.type);
        setTargetType(data.targetType);
      },
    }
  );

  // Fetch HTML templates
  const { data: templatesData } = useQuery(
    "htmlTemplates",
    () => getHtmlTemplates(),
    { select: (res) => res.data }
  );

  // Fetch plans
  const { data: plansData } = useQuery(
    "plans",
    () => getAllPlans(),
    { select: (res) => res.data }
  );

  // Handle form submission
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        description: values.description,
        reportType: values.reportType,
        htmlTemplate: values.htmlTemplate,
        targetType: values.targetType,
        includedPlans: values.targetType === "planBased" ? values.includedPlans : [],
        schedule: {
          type: values.scheduleType,
          dayOfWeek: values.scheduleType === "weekly" ? values.dayOfWeek : undefined,
          dayOfMonth: values.scheduleType === "monthly" ? values.dayOfMonth : undefined,
          timeOfDay: {
            hour: values.timeHour || 9,
            minute: values.timeMinute || 0,
          },
        },
        dataSections: {
          billing: { enabled: values.billingEnabled || false },
          usage: { enabled: values.usageEnabled || false },
          performance: { enabled: values.performanceEnabled || false },
          analytics: { enabled: values.analyticsEnabled || false },
          team: { enabled: values.teamEnabled || false },
        },
        emailSettings: {
          subject: values.emailSubject,
          fromName: values.fromName,
          replyTo: values.replyTo,
        },
      };

      if (mode === "create") {
        await createPulseReport(payload);
        message.success("Pulse report created successfully!");
      } else {
        await updatePulseReport(pulseReportId, payload);
        message.success("Pulse report updated successfully!");
      }

      queryClient.invalidateQueries("pulseReports");
      history.push("/super-admin/pulse-reports");
    } catch (error) {
      console.error("Error saving pulse report:", error);
      message.error("Failed to save pulse report");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle preview
  const handlePreview = async () => {
    if (mode !== "edit" || !pulseReportId) {
      message.info("Save the pulse report first to preview");
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await previewPulseReport(pulseReportId);
      setPreviewHtml(response.data.html);
    } catch (error) {
      console.error("Error previewing:", error);
      message.error("Failed to generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loadingPulseReport) {
    return (
      <Card style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
        <p>Loading pulse report...</p>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h3>{mode === "create" ? "Create Pulse Report" : "Edit Pulse Report"}</h3>
        </Col>
        <Col>
          <Space>
            <Button onClick={() => history.push("/super-admin/pulse-reports")}>
              Cancel
            </Button>
            {mode === "edit" && (
              <Button onClick={handlePreview} loading={previewLoading}>
                Preview
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={submitting}
            >
              {mode === "create" ? "Create" : "Save Changes"}
            </Button>
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          scheduleType: "monthly",
          targetType: "all",
          timeHour: 9,
          timeMinute: 0,
          dayOfMonth: 1,
          dayOfWeek: 1,
        }}
      >
        <Row gutter={24}>
          <Col span={16}>
            {/* Basic Information */}
            <Card title="Basic Information" style={{ marginBottom: 16 }}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter a name" }]}
              >
                <Input placeholder="e.g., Monthly Invoice Report" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <TextArea
                  rows={2}
                  placeholder="Brief description of this pulse report"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="reportType"
                    label="Report Type"
                    rules={[{ required: true, message: "Please select a type" }]}
                  >
                    <Select placeholder="Select report type">
                      {reportTypes.map((type) => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="htmlTemplate"
                    label="HTML Template"
                    rules={[{ required: true, message: "Please select a template" }]}
                  >
                    <Select
                      placeholder="Select template"
                      showSearch
                      optionFilterProp="children"
                    >
                      {templatesData?.map((template) => (
                        <Option key={template._id} value={template._id}>
                          {template.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Schedule Configuration */}
            <Card title="Schedule Configuration" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="scheduleType"
                    label="Schedule Type"
                    rules={[{ required: true }]}
                  >
                    <Select onChange={(v) => setScheduleType(v)}>
                      {scheduleTypes.map((type) => (
                        <Option key={type.value} value={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {scheduleType === "weekly" && (
                  <Col span={8}>
                    <Form.Item name="dayOfWeek" label="Day of Week">
                      <Select>
                        {daysOfWeek.map((day) => (
                          <Option key={day.value} value={day.value}>
                            {day.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {scheduleType === "monthly" && (
                  <Col span={8}>
                    <Form.Item name="dayOfMonth" label="Day of Month">
                      <InputNumber min={1} max={28} />
                    </Form.Item>
                  </Col>
                )}

                <Col span={8}>
                  <Form.Item label="Send Time">
                    <Space>
                      <Form.Item name="timeHour" noStyle>
                        <InputNumber min={0} max={23} placeholder="Hour" />
                      </Form.Item>
                      :
                      <Form.Item name="timeMinute" noStyle>
                        <InputNumber min={0} max={59} placeholder="Min" />
                      </Form.Item>
                      (UTC)
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Target Audience */}
            <Card title="Target Audience" style={{ marginBottom: 16 }}>
              <Form.Item name="targetType" label="Target Type">
                <Select onChange={(v) => setTargetType(v)}>
                  {targetTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {targetType === "planBased" && (
                <Form.Item
                  name="includedPlans"
                  label="Include Plans"
                  rules={[{ required: true, message: "Select at least one plan" }]}
                >
                  <Select mode="multiple" placeholder="Select plans">
                    {plansData?.map((plan) => (
                      <Option key={plan._id} value={plan._id}>
                        {plan.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Card>

            {/* Email Settings */}
            <Card title="Email Settings" style={{ marginBottom: 16 }}>
              <Form.Item
                name="emailSubject"
                label="Email Subject"
                rules={[{ required: true, message: "Please enter email subject" }]}
              >
                <Input placeholder="e.g., Your {{month}} Invoice from Copilotly" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="fromName" label="From Name">
                    <Input placeholder="Copilotly" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="replyTo" label="Reply To">
                    <Input placeholder="support@copilotly.com" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={8}>
            {/* Data Sections */}
            <Card title="Data Sections" style={{ marginBottom: 16 }}>
              <Alert
                message="Select which data to include in the report"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item
                name="billingEnabled"
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Billing/Invoice Data</span>
              </Form.Item>

              <Form.Item
                name="usageEnabled"
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Usage Metrics</span>
              </Form.Item>

              <Form.Item
                name="performanceEnabled"
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Chatbot Performance</span>
              </Form.Item>

              <Form.Item
                name="analyticsEnabled"
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Analytics Data</span>
              </Form.Item>

              <Form.Item
                name="teamEnabled"
                valuePropName="checked"
                style={{ marginBottom: 8 }}
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Team Data</span>
              </Form.Item>
            </Card>

            {/* Template Variables Info */}
            <Card title="Available Variables" size="small">
              <p style={{ fontSize: 12, color: "#666" }}>
                Use these variables in your template with {`{{variable_name}}`} syntax:
              </p>
              <Collapse size="small" ghost>
                <Panel header="User Variables" key="user">
                  <ul style={{ fontSize: 11, paddingLeft: 16 }}>
                    <li>user_name</li>
                    <li>user_email</li>
                    <li>user_plan</li>
                    <li>user_joined_at</li>
                  </ul>
                </Panel>
                <Panel header="Billing Variables" key="billing">
                  <ul style={{ fontSize: 11, paddingLeft: 16 }}>
                    <li>billing_plan</li>
                    <li>billing_amount</li>
                    <li>billing_period_start</li>
                    <li>billing_period_end</li>
                    <li>billing_invoice_link</li>
                  </ul>
                </Panel>
                <Panel header="Usage Variables" key="usage">
                  <ul style={{ fontSize: 11, paddingLeft: 16 }}>
                    <li>usage_chats_allotted</li>
                    <li>usage_chats_used</li>
                    <li>usage_chats_remaining</li>
                    <li>usage_percentage</li>
                  </ul>
                </Panel>
                <Panel header="Performance Variables" key="performance">
                  <ul style={{ fontSize: 11, paddingLeft: 16 }}>
                    <li>performance_total_chatbots</li>
                    <li>performance_total_conversations</li>
                    <li>performance_avg_duration</li>
                  </ul>
                </Panel>
              </Collapse>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Preview Modal */}
      {previewHtml && (
        <Card
          title="Preview"
          style={{ marginTop: 16 }}
          extra={<Button onClick={() => setPreviewHtml(null)}>Close</Button>}
        >
          <div
            style={{
              border: "1px solid #eee",
              padding: 16,
              maxHeight: 600,
              overflow: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </Card>
      )}
    </Card>
  );
};

export default PulseReportForm;
